import type { ClientTake, RelayToClient, Preset, PresetCreate, PresetUpdate, PresetFireResult, UserSummary, UserCreate, UserRole } from '@ultrix/shared'
import { RelayToClientSchema } from '@ultrix/shared'

async function jsonRequest<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    let message = `HTTP ${res.status}`
    try {
      const body = await res.json()
      if (body?.error) message = body.error
    } catch {
      // ignore
    }
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

export async function listPresets(): Promise<Preset[]> {
  const r = await jsonRequest<{ presets: Preset[] }>('/api/presets')
  return r.presets
}

export async function createPreset(input: PresetCreate): Promise<Preset> {
  const r = await jsonRequest<{ preset: Preset }>('/api/presets', {
    method: 'POST',
    body: JSON.stringify(input),
  })
  return r.preset
}

export async function updatePreset(id: number, input: PresetUpdate): Promise<Preset> {
  const r = await jsonRequest<{ preset: Preset }>(`/api/presets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
  return r.preset
}

export async function deletePreset(id: number): Promise<void> {
  await jsonRequest<{ ok: boolean }>(`/api/presets/${id}`, { method: 'DELETE' })
}

export async function firePreset(id: number): Promise<PresetFireResult> {
  return jsonRequest<PresetFireResult>(`/api/presets/${id}/fire`, { method: 'POST' })
}

export async function login(username: string, password: string): Promise<{ ok: true } | { requiresTotp: true; continuationToken: string } | { error: string }> {
  const res = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
    credentials: 'same-origin',
  })
  if (res.status === 429) return { error: 'rate_limited' }
  if (!res.ok) return { error: 'invalid_credentials' }
  return res.json()
}

export async function loginTotp(continuationToken: string, code: string): Promise<{ ok: true } | { error: string }> {
  const res = await fetch('/api/login/totp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ continuationToken, code }),
    credentials: 'same-origin',
  })
  if (!res.ok) return { error: 'invalid_code' }
  return res.json()
}

export async function logout(): Promise<void> {
  await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' })
}

export async function fetchMe(): Promise<{ username: string; role: 'admin' | 'operator' } | null> {
  const res = await fetch('/api/me', { credentials: 'same-origin' })
  if (!res.ok) return null
  return res.json()
}

export interface ClientSocketHandlers {
  onMessage: (msg: RelayToClient) => void
  onClose: () => void
  onOpen: () => void
}

export class ClientSocket {
  private ws: WebSocket | null = null
  private handlers: ClientSocketHandlers
  private pingTimer: number | null = null
  private reconnectTimer: number | null = null

  constructor(handlers: ClientSocketHandlers) {
    this.handlers = handlers
  }

  connect() {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    this.ws = new WebSocket(`${proto}//${location.host}/client`)
    this.ws.addEventListener('open', () => {
      this.handlers.onOpen()
      this.pingTimer = window.setInterval(() => {
        this.ws?.send(JSON.stringify({ type: 'ping' }))
      }, 20_000)
    })
    this.ws.addEventListener('message', (ev) => {
      try {
        const parsed = JSON.parse(ev.data)
        const result = RelayToClientSchema.safeParse(parsed)
        if (result.success) this.handlers.onMessage(result.data)
      } catch {
        // ignore
      }
    })
    this.ws.addEventListener('close', () => {
      if (this.pingTimer) clearInterval(this.pingTimer)
      this.pingTimer = null
      this.handlers.onClose()
      // Auto-reconnect after 2s
      this.reconnectTimer = window.setTimeout(() => this.connect(), 2_000)
    })
    this.ws.addEventListener('error', () => {
      this.ws?.close()
    })
  }

  sendTake(take: Omit<ClientTake, 'type'>) {
    this.ws?.send(JSON.stringify({ type: 'take', ...take }))
  }

  close() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.pingTimer) clearInterval(this.pingTimer)
    this.ws?.close()
  }
}

export async function listUsers(): Promise<UserSummary[]> {
  const r = await jsonRequest<{ users: UserSummary[] }>('/api/users')
  return r.users
}

export async function createUser(input: UserCreate): Promise<{ user: UserSummary; totpEnrollmentRequired: boolean }> {
  return jsonRequest<{ user: UserSummary; totpEnrollmentRequired: boolean }>('/api/users', { method: 'POST', body: JSON.stringify(input) })
}

export async function setUserPassword(id: number, password: string): Promise<void> {
  await jsonRequest<{ ok: boolean }>(`/api/users/${id}/password`, { method: 'POST', body: JSON.stringify({ password }) })
}

export async function patchUser(id: number, patch: { role?: UserRole; disabled?: boolean; totpRequired?: boolean; totpCode?: string }): Promise<{ user: UserSummary; totpEnrollmentRequired: boolean }> {
  return jsonRequest<{ user: UserSummary; totpEnrollmentRequired: boolean }>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export async function resetUser2fa(id: number): Promise<{ ok: boolean; totpEnrollmentRequired: boolean }> {
  return jsonRequest<{ ok: boolean; totpEnrollmentRequired: boolean }>(`/api/users/${id}/reset-2fa`, { method: 'POST' })
}

export async function deleteUser(id: number): Promise<void> {
  await jsonRequest<{ ok: boolean }>(`/api/users/${id}`, { method: 'DELETE' })
}
